import { BASE_URL_V2 } from 'src/axios/axios.constants';
import { readMirroredIdToken } from 'src/firebase/authToken.service';
import type { tEmbeddedSignupRecord } from 'src/lib/facebook/embeddedSignupTelemetry';

/**
 * Durable transport for Embedded Signup abandonment / error records (INT-1216).
 *
 * WHY `fetch` WITH `keepalive` AND NOT AxiosService
 * -------------------------------------------------
 * An abandonment correlates strongly with the customer closing the popup or
 * navigating away, and an in-flight XHR is cancelled on unload. Axios is
 * XHR-based, so routing this through AxiosService would silently lose exactly
 * the events we are trying to capture. `fetch(..., { keepalive: true })` survives
 * unload.
 *
 * `navigator.sendBeacon` also survives unload but cannot set an `Authorization`
 * header, which the BFF requires — so it is not an option either.
 *
 * DO NOT "modernize" this onto AxiosService. Going through `fetch` also keeps
 * this call outside the axios response interceptor, whose 401 arm clears
 * localStorage and redirects to `/`. A telemetry 401 must never sign the customer
 * out in the middle of a connection attempt.
 *
 * Every failure is swallowed. Telemetry is strictly best-effort and must never
 * surface to the user or influence connection state.
 */

// Short, deliberately independent of AUTHENTICATE_TIMEOUT_MS (45s) and the axios
// default (30s). A keepalive request held open for tens of seconds is wasteful,
// and nothing downstream is waiting on this response.
const TELEMETRY_TIMEOUT_MS = 5_000;

// BUG-801: the token mirror is per-tab (sessionStorage) now — the old
// localStorage key could hold whichever account logged in last on this
// browser profile. readMirroredIdToken already swallows corrupt entries;
// telemetry sends unauthenticated and lets the BFF reject it.
function readAuthToken(): string {
  return readMirroredIdToken();
}

function toRequestBody(records: tEmbeddedSignupRecord[]) {
  return {
    events: records.map((record) => ({
      attempt_id: record.attemptId,
      surface: record.surface,
      event: record.event,
      kind: record.kind,
      current_step: record.currentStep,
      error_code: record.errorCode,
      error_message: record.errorMessage,
      meta_session_id: record.metaSessionId,
      meta_timestamp: record.metaTimestamp,
      esu_version: record.version || null,
    })),
  };
}

/**
 * Posts a batch of records to the BFF. Resolves regardless of outcome — the
 * caller has nothing to do with a failure.
 */
export async function sendEmbeddedSignupEvents(
  workspaceId: string,
  records: tEmbeddedSignupRecord[],
): Promise<void> {
  if (!workspaceId || records.length === 0) return;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TELEMETRY_TIMEOUT_MS);

    try {
      await fetch(
        `${BASE_URL_V2}/whatsapp-api/workspaces/${workspaceId}/embedded-signup-events`,
        {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: readAuthToken(),
          },
          body: JSON.stringify(toRequestBody(records)),
          signal: controller.signal,
        },
      );
    } finally {
      // Always clear the timer, including on abort, so no handle is left dangling.
      clearTimeout(timeoutId);
    }
  } catch {
    // Network failure, abort, offline, or fetch being unavailable — all no-ops.
  }
}
